import React from 'react'
import { keyframes } from '@emotion/react'
import { AppImages, variantProvider } from '@/app'
import { modal, ModalParams } from '@codeleap/modals'
import { Image, Text, View } from '@/components'
import { SuccessPersonaSelected } from './SuccessPersonaSelected'
import { APIClient, useSharedSettings } from '@/services'
import { TypeGuards } from '@codeleap/types'

const DEFAULT_DESCRIPTION = "We're plotting a course that's the closest fit for you."
const DEFAULT_HERO = 'Personalising your journey...'
const IMAGE_SIZE = 300

const pulse = keyframes({
  '0%': { transform: 'scale(1)' },
  '50%': { transform: 'scale(1.2)' },
  '100%': { transform: 'scale(1)' },
})

function Content(props: ModalParams) {
  const { toggle } = props

  const sharedSettings = useSharedSettings()

  const hero = React.useMemo(() => {
    if (!TypeGuards.isString(sharedSettings.settings?.learn.persona.onboardingHero)) return DEFAULT_HERO
    return sharedSettings.settings.learn.persona.onboardingHero
  }, [sharedSettings.settings?.learn.persona.onboardingHero])

  const description = React.useMemo(() => {
    if (!TypeGuards.isString(sharedSettings.settings?.learn.persona.onboardingDescription)) return DEFAULT_DESCRIPTION
    return sharedSettings.settings.learn.persona.onboardingDescription
  }, [sharedSettings.settings?.learn.persona.onboardingDescription])

  const delay = React.useMemo(() => {
    if (!TypeGuards.isNumber(sharedSettings.settings?.learn.persona.loadingTime)) return 6000
    return sharedSettings.settings.learn.persona.loadingTime
  }, [sharedSettings.settings?.learn.persona.loadingTime])

  React.useEffect(() => {
    const timer = setTimeout(() => {
      toggle()
      SuccessPersonaSelected.toggle()
    }, delay)
    APIClient.Learn.groupsManager.queryKeys.refetchList()
    return () => clearTimeout(timer)
  }, [toggle, delay])

  return (
    <View style={styles.wrapper}>
      <Text variants={['h1', 'color:neutral1']} text={hero} />

      <Image source={AppImages.pulseAnimation} css={styles.pulseImage} />

      <Text variants={['h2', 'color:neutral1', 'textCenter', 'marginHorizontal:2']} text={description} />
    </View>
  )
}

const styles = variantProvider.createComponentStyle(
  t => ({
    wrapper: {
      ...t.presets.fullWidth,
      ...t.presets.column,
      ...t.presets.center,
      height: 700,
      backgroundImage: `url(${AppImages.appBlueBg})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      ...t.spacing.padding(3),
      ...t.spacing.gap(3),
    },
    pulseImage: {
      height: IMAGE_SIZE,
      width: 'auto',
      maxWidth: '100%',
      objectFit: 'contain',
      animation: `${pulse} 1600ms ease-in-out infinite`,
      transformOrigin: 'center',
      display: 'block',
    },
  }),
  true,
)

export const OnboardingSuccessModal = modal()
  .content(props => <Content {...props} />)
  .props({
    variants: ['noPadding', 'centered', 'width:700'],
    closable: false,
  })
